import Button from '@fff-web/fff-ui-shared/Button';
import { Spinner } from '@fff-web/fff-ui-shared/Icon';
import classNames from 'classnames';
import React from 'react';
import { ICoupon, IGiftCard } from '../../../interfaces/coupon';
import { CTA_APPLY_COUPON } from '../../utils/buttonCTA';
import { generateCouponText } from '../../utils/generateCouponText';
import {
  CI_FORM,
  CI_INPUT,
  CI_INPUT_WRAPPER,
  CI_LABEL,
} from '../Recurly/CouponInput/couponInputTestIds';
import { FieldError } from '../Shared';
import styles from './UniversalCouponInput.module.css';

const PLACEHOLDER = 'Enter Promo Code or Gift Card';
const LABEL = 'Enter Promo Code';

interface UniversalCouponInputProps {
  coupon: string;
  handleOnChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  label?: string;
  error: boolean | null;
  loading: boolean;
  handleCallCouponApi: () => void;
  variant?: 'default' | 'secondary';
  couponData: IGiftCard | ICoupon | null;
  discount?: string | number;
}
const DEFAULT_DISCOUNT = '0.00';

const UniversalCouponInput: React.FunctionComponent<UniversalCouponInputProps> = ({
  coupon,
  handleOnChange,
  placeholder,
  error,
  loading,
  label,
  handleCallCouponApi,
  couponData,
  variant = 'default',
  discount,
}) => {
  const isDefault = variant === 'default';
  const invalidInputStyles = error ? ` ${styles.couponInputInvalid}` : '';
  const shouldShowCouponText = !isDefault && !error && couponData;

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    handleCallCouponApi();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleCallCouponApi();
  };

  return (
    <form data-testid={CI_FORM} onSubmit={handleSubmit} className={styles.couponInputContainer}>
      <label data-testid={CI_LABEL} htmlFor="couponCode" className={styles.couponInputLabel}>
        <div className={styles.discounAndLabel}>
          {label ?? LABEL} <p>{(!isDefault && discount) || DEFAULT_DISCOUNT}</p>
        </div>
        <div
          data-testid={CI_INPUT_WRAPPER}
          className={classNames({
            [styles.defaultInputWrapper]: isDefault,
            [styles.secondaryInputWrapper]: !isDefault,
          })}
        >
          <input
            data-testid={CI_INPUT}
            className={`${styles.couponInput}${invalidInputStyles}`}
            type="text"
            name="couponCode"
            value={coupon}
            disabled={loading}
            onChange={handleOnChange}
            placeholder={placeholder ?? PLACEHOLDER}
            onBlur={handleInputBlur}
          />
          <Button
            type="submit"
            disabled={loading}
            variation={isDefault ? 'primary-outline' : 'primary'}
          >
            {isDefault ? CTA_APPLY_COUPON : <Spinner />}
          </Button>
        </div>
        {shouldShowCouponText && (
          <p className={styles.successMessage}>{generateCouponText(couponData)}</p>
        )}
        {error && <FieldError errorMsg="This promo code is invalid" />}
      </label>
    </form>
  );
};

export default UniversalCouponInput;
